import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

/**
 * Clayglass button variants.
 * Default variant applies the raised clay-button effect.
 */
const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-xl text-sm font-medium transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground clay-button hover:bg-primary/90",
        destructive:
          "bg-destructive text-destructive-foreground clay-button hover:bg-destructive/90",
        outline:
          "border border-input bg-background/60 backdrop-blur-sm hover:bg-accent hover:text-accent-foreground clay-button",
        secondary:
          "bg-secondary text-secondary-foreground clay-button hover:bg-secondary/80",
        ghost:
          "hover:bg-accent hover:text-accent-foreground rounded-xl",
        link:
          "text-primary underline-offset-4 hover:underline rounded-xl",
      },
      size: {
        default: "h-10 px-5 py-2",
        xs: "h-8 px-3 text-xs",
        sm: "h-9 px-3.5 text-xs",
        lg: "h-11 px-8 text-base",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  /** When true, merges props onto the single child element instead of rendering a <button>. */
  asChild?: boolean;
}

/** Clayglass-themed button with raised 3D clay effect. */
const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, children, ...props }, ref) => {
    const classes = cn(buttonVariants({ variant, size, className }));

    if (asChild && React.isValidElement(children)) {
      return React.cloneElement(children as React.ReactElement<Record<string, unknown>>, {
        className: cn(classes, (children.props as Record<string, unknown>).className as string | undefined),
        ref,
        ...props,
      });
    }

    return (
      <button className={classes} ref={ref} {...props}>
        {children}
      </button>
    );
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };
